import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../context/ThemeContext';
import { billingAPI, Subscription } from '../../services/api';

interface SubscriptionStatusProps {
  onManageClick?: () => void;
  onUpgradeClick?: () => void;
}

export const SubscriptionStatus: React.FC<SubscriptionStatusProps> = ({
  onManageClick,
  onUpgradeClick,
}) => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [trialing, setTrialing] = useState(false);
  const [trialDaysRemaining, setTrialDaysRemaining] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadSubscription();
  }, []);

  const loadSubscription = async () => {
    try {
      const response = await billingAPI.getCurrentSubscription();
      setSubscription(response.data.subscription);
      setTrialing(response.data.trialing);
      setTrialDaysRemaining(response.data.trial_days_remaining);
    } catch (err) {
      setError(t('billing.errors.loadSubscription'));
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'active':
        return isDark ? 'bg-emerald-500/20 text-emerald-400' : 'bg-green-100 text-green-800';
      case 'trialing':
        return isDark ? 'bg-cyan-500/20 text-cyan-400' : 'bg-blue-100 text-blue-800';
      case 'past_due':
        return isDark ? 'bg-amber-500/20 text-amber-400' : 'bg-yellow-100 text-yellow-800';
      case 'canceled':
        return isDark ? 'bg-red-500/20 text-red-400' : 'bg-red-100 text-red-800';
      default:
        return isDark ? 'bg-slate-600/50 text-slate-300' : 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <div className={`rounded-xl shadow-lg p-6 animate-pulse ${isDark ? 'bg-slate-800 border border-slate-700' : 'bg-white'}`}>
        <div className={`h-6 rounded w-1/3 mb-4 ${isDark ? 'bg-slate-700' : 'bg-gray-200'}`}></div>
        <div className={`h-4 rounded w-1/2 mb-2 ${isDark ? 'bg-slate-700' : 'bg-gray-200'}`}></div>
        <div className={`h-4 rounded w-2/3 ${isDark ? 'bg-slate-700' : 'bg-gray-200'}`}></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`px-4 py-3 rounded-xl ${isDark ? 'bg-red-500/10 border border-red-500/30 text-red-400' : 'bg-red-50 border border-red-200 text-red-700'}`}>
        {error}
      </div>
    );
  }

  if (!subscription) {
    return (
      <div className={`rounded-xl shadow-lg p-6 ${isDark ? 'bg-slate-800 border border-slate-700' : 'bg-white'}`}>
        <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          {t('billing.noSubscription')}
        </h3>
        <p className={`mb-4 ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>
          {t('billing.noSubscriptionDescription')}
        </p>
        {onUpgradeClick && (
          <button
            onClick={onUpgradeClick}
            className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-4 py-2 rounded-lg hover:from-cyan-400 hover:to-blue-500 transition-all"
          >
            {t('billing.choosePlan')}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className={`rounded-xl shadow-lg p-6 ${isDark ? 'bg-slate-800 border border-slate-700' : 'bg-white'}`}>
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {subscription.plan?.name || t('billing.subscription')}
          </h3>
          <span
            className={`inline-block px-2 py-1 text-xs font-semibold rounded-full mt-1 ${getStatusColor(
              subscription.status
            )}`}
          >
            {t(`billing.status.${subscription.status}`)}
          </span>
        </div>
        {onManageClick && (
          <button
            onClick={onManageClick}
            className="text-cyan-500 hover:text-cyan-400 text-sm font-medium transition-colors"
          >
            {t('billing.manage')}
          </button>
        )}
      </div>

      {trialing && trialDaysRemaining > 0 && (
        <div className={`rounded-lg p-3 mb-4 ${isDark ? 'bg-cyan-500/20 border border-cyan-500/40' : 'bg-blue-50 border border-blue-200'}`}>
          <p className={`text-sm ${isDark ? 'text-cyan-300' : 'text-blue-800'}`}>
            {t('billing.trialDaysRemaining', { days: trialDaysRemaining })}
          </p>
        </div>
      )}

      {subscription.cancel_at_period_end && (
        <div className={`rounded-lg p-3 mb-4 ${isDark ? 'bg-amber-500/20 border border-amber-500/40' : 'bg-yellow-50 border border-yellow-200'}`}>
          <p className={`text-sm ${isDark ? 'text-amber-300' : 'text-yellow-800'}`}>
            {t('billing.cancelAtPeriodEnd', {
              date: formatDate(subscription.current_period_end),
            })}
          </p>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4 text-sm">
        <div>
          <p className={isDark ? 'text-slate-400' : 'text-gray-500'}>{t('billing.seats')}</p>
          <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{subscription.quantity}</p>
        </div>
        <div>
          <p className={isDark ? 'text-slate-400' : 'text-gray-500'}>{t('billing.monthlyPrice')}</p>
          <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
            ${(subscription.monthly_price / 100).toFixed(2)}
          </p>
        </div>
        <div>
          <p className={isDark ? 'text-slate-400' : 'text-gray-500'}>{t('billing.currentPeriod')}</p>
          <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {formatDate(subscription.current_period_start)} -{' '}
            {formatDate(subscription.current_period_end)}
          </p>
        </div>
        {subscription.plan?.user_limit && (
          <div>
            <p className={isDark ? 'text-slate-400' : 'text-gray-500'}>{t('billing.userLimit')}</p>
            <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {subscription.plan.user_limit * subscription.quantity}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionStatus;
